import { useQuery } from '@apollo/client';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import * as qs from 'query-string';
import { getDomainConfig } from '../../lib/domainConfig';
import { getLoadingOrErrorElement } from '../../lib/util';
import { gqlTypes } from '../../types';
import { SimpleDismissableModal } from '../../components/modals';
import * as events from '../../lib/analytics/events';
import FiLogo from '../../assets/images/logo-qr-code.svg';
import { QRCode } from 'react-qrcode-logo';
import { ReactComponent as LinkIcon } from '../../assets/images/icons/icon_link.svg';
import { referralsQuery } from '../../graphql-operations';
import InviteList from './components/InviteList/InviteList';
import styles from './Referrals.module.scss';

export const getShareInformation = (code: string, message: string, t: (key: string) => string) => ({
  title: t('join_the_pack'),
  text: message,
  url: getCodeUrl(code),
});

interface ReferralCodeProps {
  code: string;
  message: string;
}

function WebShareButton({ code, message }: ReferralCodeProps) {
  const { t } = useTranslation();
  const handleShare = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    events.referrals.shareClicked(true);
    // Web share API: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
    (navigator as any).share(getShareInformation(code, message, t));
  };

  return (
    <div className={styles.shareButton} onClick={handleShare}>
      <div className={styles.code}>
        <div className={styles.heading}>{t('your_invite_code')}</div>
        <div>{code}</div>
      </div>
      <div className={styles.icon}>
        <LinkIcon />
      </div>
    </div>
  );
}

function AndroidShareButton({ code, message }: ReferralCodeProps) {
  const { t } = useTranslation();
  const handleShare = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    events.referrals.shareClicked(true);
    (window as any).android.share(JSON.stringify(getShareInformation(code, message, t)));
  };

  return (
    <div className={styles.shareButton} onClick={handleShare}>
      <div className={styles.code}>
        <div className={styles.heading}>{t('your_invite_code')}</div>
        <div>{code}</div>
      </div>
      <div className={styles.icon}>
        <LinkIcon />
      </div>
    </div>
  );
}

// This will be used if a native share integration is not available
function FallbackShareButton({ code, message }: ReferralCodeProps) {
  const { t } = useTranslation();
  const shareInformation = getShareInformation(code, message, t);
  const mailtoLink = `mailto:?${qs.stringify({
    subject: shareInformation.title,
    body: shareInformation.text,
  })}`;

  return (
    <SimpleDismissableModal
      trigger={
        <div className={styles.shareButton}>
          <div className={styles.code}>
            <div className={styles.heading}>{t('your_invite_code')}</div>
            <div>{code}</div>
          </div>
          <div className={styles.icon}>
            <LinkIcon />
          </div>
        </div>
      }
      onOpen={() => events.referrals.shareClicked(false)}
    >
      <div>
        <a href={mailtoLink} target="_blank" rel="noopener noreferrer">
          {t('share_via_email')}
        </a>
      </div>
    </SimpleDismissableModal>
  );
}

// Determine which type of sharing integration is available on the client platform
function ShareButton({ code, message }: ReferralCodeProps): JSX.Element {
  if ((navigator as any).share) {
    // View supports native sharing https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
    return <WebShareButton code={code} message={message} />;
  } else if ((window as any).android && (window as any).android.share) {
    // Use android specific native sharing
    return <AndroidShareButton code={code} message={message} />;
  } else {
    // No native sharing exists
    return <FallbackShareButton code={code} message={message} />;
  }
}

function getCodeUrl(code: string): string {
  return `https://${getDomainConfig().shopDomain}/r/${code}/?utm_source=referrals`;
}

interface ProgramDetailsProps {
  totalDollarsEarned?: number;
  numActiveReferrals?: number;
}

function ProgramDetails({ totalDollarsEarned, numActiveReferrals }: ProgramDetailsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <div className={styles.give1MonthGet1DollarPerMonthDetails}>
        {totalDollarsEarned && totalDollarsEarned > 0 && (
          <div className={styles.give1MonthGet1DollarPerMonthDetailsTitle}>
            <h3>{t('congrats')}</h3>
            <p>{t('you_earned_dollars_in_fi_credits', { amount: totalDollarsEarned })}</p>
          </div>
        )}
        {!totalDollarsEarned && (
          <div className={styles.give1MonthGet1DollarPerMonthDetailsTitle}>
            <h3>{t('you_have_5_invitations')}</h3>
            <p>{t('invite_your_friends_and_earn_fi_credit')}</p>
          </div>
        )}
        {numActiveReferrals && numActiveReferrals > 0 && (
          <div className={styles.give1MonthGet1DollarPerMonthDetailsPendingEarn}>
            {t('congrats_you_have_active_referrals_amount_will_be_added', {
              count: numActiveReferrals,
              amount: numActiveReferrals,
            })}
          </div>
        )}
      </div>
      <hr />
    </>
  );
}

export default function Referrals() {
  const { t } = useTranslation();
  // Retrieve current user's referral information
  const { loading, error, data } = useQuery<gqlTypes.ECOMMERCE_referrals>(referralsQuery);

  let finalError: Error | undefined = error;
  if (data && data.currentUser.referrals.codeSuspended) {
    finalError = new Error(t('that_referral_code_has_been_suspended'));
  }

  const loadingOrErrorElement = getLoadingOrErrorElement(loading, finalError);
  if (loadingOrErrorElement) {
    return loadingOrErrorElement;
  }

  if (!data) {
    return <>{t('no_referral_code_was_found')}</>;
  }

  const { referralCode, referralMessage, referralProgramName, give1MonthGet1DollarPerMonthDetails, invitedReferees } =
    data.currentUser.referrals;

  return (
    <div className={styles.main}>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0" />
      </Helmet>
      <div className={styles.content}>
        {referralProgramName === 'Give1MonthGet1DollarPerMonthNoLimit' &&
          give1MonthGet1DollarPerMonthDetails &&
          (give1MonthGet1DollarPerMonthDetails.totalDollarsEarned > 0 ||
            give1MonthGet1DollarPerMonthDetails.numActiveReferrals > 0) && (
            <ProgramDetails
              numActiveReferrals={give1MonthGet1DollarPerMonthDetails.numActiveReferrals}
              totalDollarsEarned={give1MonthGet1DollarPerMonthDetails.totalDollarsEarned}
            />
          )}
        {referralProgramName === 'Give1MonthGet1DollarPerMonthWithLimit' && (
          <>
            <ProgramDetails
              totalDollarsEarned={give1MonthGet1DollarPerMonthDetails?.totalDollarsEarned}
              numActiveReferrals={give1MonthGet1DollarPerMonthDetails?.numActiveReferrals}
            />
            <InviteList
              maxInvites={5}
              invites={invitedReferees.map((referee) => ({
                name: referee.firstName,
                profileImageUrl: referee.profilePicture?.nearestResized,
              }))}
              code={referralCode}
              message={referralMessage}
            />
          </>
        )}
        <h2>{t('invite_your_friends')}</h2>
        <p>
          {referralProgramName === 'Give1MonthGet1DollarPerMonthNoLimit' ||
          referralProgramName === 'Give1MonthGet1DollarPerMonthWithLimit'
            ? t('invite_friends_to_fi_and_earn_up_to_amount_per_referral', { amount: 100 })
            : t('invite_more_friends_to_fi_and_get_free_month')}
        </p>
        <p className={styles.scanOrShareCode}>{t('scan_or_share_code')}</p>
        <div className={styles.qrCodeWrapper}>
          <QRCode
            size={140}
            value={getCodeUrl(referralCode)}
            logoImage={FiLogo}
            logoWidth={40}
            logoHeight={40}
            quietZone={0}
            removeQrCodeBehindLogo={false}
            qrStyle="squares"
          />
        </div>

        <ShareButton code={referralCode} message={referralMessage} />

        {(referralProgramName === 'Give1MonthGet1DollarPerMonthNoLimit' ||
          referralProgramName === 'Give1MonthGet1DollarPerMonthWithLimit') && (
          <p className={styles.footerDescription}>
            {t('every_friend_you_refer_will_contribute_amount_to_your_fi_balance', { amount: 1, maxAmount: 100 })}
          </p>
        )}
      </div>
    </div>
  );
}
